import React from 'react';
import PropTypes from 'prop-types';
import { ListGroup, ListGroupItem, Spinner } from 'reactstrap';
import NeoButton from './NeoButton';
import NeoBadge from './NeoBadge';
import NeoCard from './NeoCard';

/**
 * NeoSideMenu
 * Componente premium de menu lateral / navegação em sidebar.
 * Suporta títulos, subtítulos, ícones circulares outonais,
 * badges dinâmicas, subtextos de descrição e transições 3D.
 */
const NeoSideMenu = ({
    title = '',
    subtitle = '',
    icon = '',
    items = [],
    activeId = '',
    onSelect = () => { },
    loading = false,
    onAdd = null,
    onAddText = '',
    headerAction = null,
    searchComponent = null,
    footerActions = null,
    children = null,
    className = '',
    style = {},
    ...props
}) => {
    return (
        <NeoCard
            className={`neo-side-menu-card ${className}`}
            style={{
                display: 'flex',
                flexDirection: 'column',
                height: '100%',
                ...style
            }}
            bodyClassName="p-0 d-flex flex-column h-100"
            noBorder={true} // A classe neo-side-menu-card já gerencia as bordas e posicionamento do menu
            {...props}
        >
            {/* Cabeçalho do Menu */}
            {(title || subtitle) && (
                <div className="bg-white border-bottom py-3 px-3 flex-shrink-0" style={{ borderBottom: "2px solid rgba(72, 39, 18, 0.12)" }}>
                    <div className="d-flex align-items-center justify-content-between w-100">
                        <div className="d-flex align-items-center overflow-hidden">
                            {icon && (
                                <div className="neo-section-icon-square flex-shrink-0 me-3">
                                    <i className={`mdi ${icon} font-size-18 text-accent`} />
                                </div>
                            )}
                            <div className="ms-0 overflow-hidden text-start">
                                {title && <h6 className="mb-0 fw-bold text-dark text-truncate neo-font-outfit font-size-15">{title}</h6>}
                                {subtitle && <p className="text-muted small mb-0 text-truncate font-size-12">{subtitle}</p>}
                            </div>
                        </div>
                        {(onAdd || headerAction) && (
                            <div className="flex-shrink-0 ms-2">
                                {onAdd ? (
                                    <NeoButton
                                        variant="primary"
                                        size="md"
                                        square={true}
                                        icon="mdi-plus"
                                        title="Adicionar Novo"
                                        onClick={onAdd}
                                    />
                                ) : (
                                    headerAction
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Slot de Busca */}
            {searchComponent && (
                <div className="px-3 pt-3 pb-1 flex-shrink-0 border-bottom bg-white" style={{ borderBottom: "1.5px solid rgba(72, 39, 18, 0.08)" }}>
                    {searchComponent}
                </div>
            )}

            {/* Corpo / Itens */}
            <div className="flex-grow-1 overflow-auto custom-scrollbar py-3">
                {loading ? (
                    <div className="text-center py-5">
                        <Spinner color="primary" size="sm" />
                        <p className="text-muted small mt-2 mb-0">Carregando...</p>
                    </div>
                ) : children ? (
                    children
                ) : (
                    <ListGroup flush>
                        {items.map((item) => {
                            const isActive = activeId === item.id;
                            const badgeVariant = item.badgeVariant || 'success';


                            return (
                                <div className="px-2 mb-1.5" key={item.id}>
                                    <ListGroupItem
                                        tag="button"
                                        action
                                        onClick={() => onSelect(item.id)}
                                        className={`border-0 py-3 px-3 d-flex align-items-center justify-content-between select-none neo-side-menu-item ${isActive ? 'neo-side-menu-item--active' : ''}`}
                                    >
                                        <div className="d-flex align-items-center flex-grow-1 overflow-hidden">
                                            {(item.photoUrl || item.icon || item.color) && (
                                                <div className="side-menu-icon-wrapper me-3 flex-shrink-0">
                                                    {item.photoUrl ? (
                                                        <img
                                                            src={item.photoUrl}
                                                            alt={item.label}
                                                            style={{
                                                                width: '100%',
                                                                height: '100%',
                                                                borderRadius: '8px',
                                                                objectFit: 'cover'
                                                            }}
                                                        />
                                                    ) : item.color ? (
                                                        <div
                                                            style={{
                                                                width: '12px',
                                                                height: '12px',
                                                                borderRadius: '50%',
                                                                backgroundColor: item.color,
                                                                border: '1.5px solid rgba(72, 39, 18, 0.15)'
                                                            }}
                                                        />
                                                    ) : (
                                                        <i className={`mdi ${item.icon} font-size-16`}></i>
                                                    )}
                                                </div>
                                            )}
                                            <div className="text-start overflow-hidden">
                                                <h6 className="mb-0 fw-bold text-truncate font-size-13 side-menu-title">
                                                    {item.label}
                                                </h6>
                                                {item.description && (
                                                    <span className="text-muted small font-size-11 d-block text-truncate">
                                                        {item.description}
                                                    </span>
                                                )}
                                            </div>
                                        </div>

                                        {item.badge && (
                                            <NeoBadge color={badgeVariant}>
                                                {item.badge}
                                            </NeoBadge>
                                        )}
                                    </ListGroupItem>
                                </div>
                            );
                        })}

                        {items.length === 0 && (
                            <div className="text-center py-5 text-muted small">
                                Nenhum item encontrado.
                            </div>
                        )}
                    </ListGroup>
                )}
            </div>

            {/* Slot de Ações do Rodapé */}
            {footerActions && (
                <div className="p-3 border-top bg-light bg-opacity-30 flex-shrink-0">
                    {footerActions}
                </div>
            )}
        </NeoCard>
    );
};

NeoSideMenu.propTypes = {
    title: PropTypes.string,
    subtitle: PropTypes.string,
    icon: PropTypes.string,
    items: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            label: PropTypes.string.isRequired,
            icon: PropTypes.string,
            photoUrl: PropTypes.string,
            color: PropTypes.string,
            description: PropTypes.string,
            badge: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
            badgeVariant: PropTypes.oneOf(['success', 'accent', 'chocolate', 'danger', 'muted']),
        })
    ),
    activeId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    onSelect: PropTypes.func,
    loading: PropTypes.bool,
    onAdd: PropTypes.func,
    onAddText: PropTypes.string,
    headerAction: PropTypes.element,
    searchComponent: PropTypes.element,
    footerActions: PropTypes.element,
    children: PropTypes.node,
    className: PropTypes.string,
    style: PropTypes.object,
};

export default NeoSideMenu;
